import { AnyObject } from 'common/src/utils/typescript-utils';
import { isEmpty } from 'lodash';
import { Children, cloneElement, createElement, isValidElement, ReactElement, ReactNode } from 'react';
import { markdownStyles } from 'utils/markdown-styles';

interface ComponentProps {
  children: ReactNode;
  props: AnyObject;
}

function isElementOfType<P>(element: ReactNode, type: string): element is ReactElement<P> {
  return isValidElement(element) && element.type === type;
}

// Markdown tables should always have a header row, because some parsers will not render it as a table if it's missing
// To overcome this, we filter out the header row if all its cells are empty when converting to html
function filterEmptyTableHeaderRow(children: ReactNode) {
  return Children.toArray(children).map((child) => {
    if (isElementOfType(child, 'thead')) {
      // Replace the thead with a new thead that has no empty header row
      return cloneElement(child, {
        ...child.props,
        children: Children.toArray(child.props.children)
          .map((row) => {
            if (!isElementOfType(row, 'tr')) {
              return row;
            }

            // If there is at least one non-empty header cell, return the row
            if (
              Children.toArray(row.props.children).some(
                (cell) => isElementOfType(cell, 'th') && !isEmpty(cell.props.children),
              )
            ) {
              return row;
            }

            return null;
          })
          .filter(Boolean), // Remove null rows
      });
    }

    return child;
  });
}

export function markdownOverrides() {
  return {
    ...markdownStyles,
    table: {
      ...markdownStyles.table,
      component: ({ children, ...props }: ComponentProps) => {
        const filteredChildren = filterEmptyTableHeaderRow(children);
        return createElement('table', props, filteredChildren);
      },
    },
  };
}
